/* eslint-disable */
import { useMutation, useQuery } from '@apollo/client';
import { cloneDeep } from 'lodash';
import * as React from 'react';
import {
  CreateWeeklyHeatingSchedule,
  CreateWeeklyHeatingScheduleMutation,
  CreateWeeklyHeatingScheduleMutationVariables,
  DayHeatingSchedules,
  GetWeeklyHeatingSchedule,
  GetWeeklyHeatingScheduleQuery,
  GetWeeklyHeatingScheduleQueryVariables,
  HeatingScheduleInputs,
  HeatingSchedulesByDayResponse,
  HeatingSchedulesResponse,
  WeeklyHeatingScheduleInputs,
} from '../../../shared/api-clients/gql';
import { IZoneArray, mergeUiSchedules } from '../helpers/scheduleHelper';
import { ScheduleMutator } from './Schedules';

export enum Day {
  monday = 'Mon',
  tuesday = 'Tue',
  wednesday = 'Wed',
  thursday = 'Thu',
  friday = 'Fri',
  saturday = 'Sat',
  sunday = 'Sun',
}

export const DayFullName = {
  Mon: 'Monday',
  Tue: 'Tuesday',
  Wed: 'Wednesday',
  Thu: 'Thursday',
  Fri: 'Friday',
  Sat: 'Saturday',
  Sun: 'Sunday',
};

export interface IHeatingSchedulesContext extends ScheduleMutator {
  heatingSchedules: HeatingSchedulesResponse;
  setDay: (day: Day) => void;
  day: Day;
  heatingMutationInProgress: boolean;
}
interface IZones {
  active: boolean;
  zone_id: string;
  temperature: string | number;
  description: string;
  heating_state: string;
  lower_bounds: number;
  upper_bounds: number;
  setpoint: number;
}
export interface IMultiSchedule {
  startTime: string;
  endTime: string;
  days: string[];
  zones: IZones[];
}
const defaultObj = {
  monday: [],
  tuesday: [],
  wednesday: [],
  thursday: [],
  friday: [],
  saturday: [],
  sunday: [],
};
export const HeatingSchedulesContext =
  React.createContext<IHeatingSchedulesContext>(undefined);

export const HeatingSchedulesProvider = ({
  children,
  deviceId,
}: React.PropsWithChildren<{ deviceId: string }>) => {
  const [heatingSchedules, setHeatingSchedules] =
    React.useState<HeatingSchedulesResponse>({});
  const [day, setDay] = React.useState(null);

  const { data: allHeatingSchedules } = useQuery<
    GetWeeklyHeatingScheduleQuery,
    GetWeeklyHeatingScheduleQueryVariables
  >(GetWeeklyHeatingSchedule, {
    fetchPolicy: 'network-only',
    pollInterval: 1000,
    variables: {
      deviceId,
    },
    onError: (error) => console.log(error),
  });

  const [createWeeklyHeatingSchedule, { loading: heatingMutationInProgress }] =
    useMutation<
      CreateWeeklyHeatingScheduleMutation,
      CreateWeeklyHeatingScheduleMutationVariables
    >(CreateWeeklyHeatingSchedule);

  React.useEffect(() => {
    if (allHeatingSchedules?.getWeeklyHeatingSchedule) {
      setHeatingSchedules(allHeatingSchedules?.getWeeklyHeatingSchedule);
    }
  }, [allHeatingSchedules]);

  const buildAndCreateHeatingSchedule = async (
    newHeatingSchedules: HeatingSchedulesResponse
  ) => {
    const createHeatingScheduleRequest: WeeklyHeatingScheduleInputs = {};

    for (const key in newHeatingSchedules) {
      createHeatingScheduleRequest[key] = newHeatingSchedules?.[key]?.reduce(
        (
          acc: HeatingScheduleInputs[],
          schedule: HeatingSchedulesByDayResponse
        ) => {
          acc.push({
            setpoints: mergeUiSchedules(schedule?.schedules),
            zone_id: schedule.zone_id,
          });
          return acc;
        },
        []
      );
    }

    await createWeeklyHeatingSchedule({
      variables: {
        deviceId,
        heatingSchedule: createHeatingScheduleRequest,
      },
    });
  };

  const editMultipleSchedules = async (zoneArray: IZoneArray) => {
    const heatingSchedulesToMutate = cloneDeep(defaultObj);

    for (const zones in zoneArray) {
      const data = zoneArray[zones];
      for (const item of data) {
        for (const day of item.day) {
          const schedule = heatingSchedulesToMutate[day];
          const scheduleZones = schedule.find((s) => s.zone_id === zones);
          const scheduleObj: DayHeatingSchedules = {
            start: item.start,
            end: item.end,
            temperature: +item.temperature,
          };
          if (!scheduleZones) {
            schedule.push({
              zone_id: zones,
              schedules: [scheduleObj],
            });
          } else {
            if (!scheduleZones.schedules) scheduleZones.schedules = [];
            scheduleZones.schedules.push(scheduleObj);
          }
        }
      }
    }
    await buildAndCreateHeatingSchedule(heatingSchedulesToMutate);
  };

  const addMultipleSchedules = async (newSchedules: IMultiSchedule) => {
    // Add new schedules for every selected zone to existing schedules
    const heatingSchedulesToMutate = cloneDeep({
      monday: heatingSchedules.monday ?? [],
      tuesday: heatingSchedules.tuesday ?? [],
      wednesday: heatingSchedules.wednesday ?? [],
      thursday: heatingSchedules.thursday ?? [],
      friday: heatingSchedules.friday ?? [],
      saturday: heatingSchedules.saturday ?? [],
      sunday: heatingSchedules.sunday ?? [],
    });
    for (const { temperature, zone_id } of newSchedules.zones) {
      const scheduleObj: DayHeatingSchedules = {
        start: newSchedules.startTime,
        end: newSchedules.endTime,
        temperature: +temperature,
      };
      for (const day of newSchedules.days) {
        const schedule = heatingSchedulesToMutate[day];
        const scheduleZones = schedule.find((s) => s.zone_id === zone_id);

        if (!scheduleZones) {
          schedule.push({
            zone_id: zone_id,
            schedules: [scheduleObj],
          });
        } else {
          if (!scheduleZones.schedules) scheduleZones.schedules = [];
          scheduleZones.schedules.push(scheduleObj);
        }
      }
    }

    await buildAndCreateHeatingSchedule(heatingSchedulesToMutate);
  };

  return (
    <HeatingSchedulesContext.Provider
      value={{
        heatingSchedules,
        setDay,
        day,
        heatingMutationInProgress,
        addMultipleSchedules,
        editMultipleSchedules,
      }}
    >
      {children}
    </HeatingSchedulesContext.Provider>
  );
};

export const useHeatingSchedules = () =>
  React.useContext(HeatingSchedulesContext);
